import React, { useCallback, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Button, Icon } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import { Draggable } from 'react-beautiful-dnd';
import { usePopup } from '../../lib/popup';
import { useTranslation } from 'react-i18next';

import { startStopwatch, stopStopwatch } from '../../utils/stopwatch';
import Paths from '../../constants/Paths';
import Tasks from './Tasks';
import NameEdit from './NameEdit';
import ActionsStep from './ActionsStep';
import User from '../User';
import Label from '../Label';
import Stopwatch from '../Stopwatch';
import PointsBadge from './PointsBadge';
import HiddenPointAccrualsBadge from './HiddenPointAccrualsBadge';

import styles from './Card.module.scss';
import dueDateStyles from '../DueDate/DueDate.module.scss';

const Card = React.memo(
  ({
    id,
    index,
    name,
    dueDate,
    createdAt,
    stopwatch,
    coverUrl,
    boardId,
    listId,
    projectId,
    isPersisted,
    notificationsTotal,
    users,
    labels,
    tasks,
    allProjectsToLists,
    allBoardMemberships,
    allLabels,
    canEdit,
    isClosed,
    pointsData,
    taskClosedAt,
    isPointSystemEnabled,
    closedUnderPreviousTemplate,
    previousTemplateName,
    hiddenPointAccrualsTemplateName,
    onUpdate,
    onMove,
    onTransfer,
    onDelete,
    onArchive,
    onClone,
    onUserAdd,
    onUserRemove,
    onBoardFetch,
    onLabelAdd,
    onLabelRemove,
    onLabelCreate,
    onLabelUpdate,
    onLabelMove,
    onLabelDelete,
  }) => {

    const nameEdit = useRef(null);
    
    const handleClick = useCallback(() => {
      if (document.activeElement) {
        document.activeElement.blur();
      }
    }, []);

    const handleToggleStopwatchClick = useCallback(
      (event) => {
        event.preventDefault();

        onUpdate({
          stopwatch: stopwatch.startedAt ? stopStopwatch(stopwatch) : startStopwatch(stopwatch),
        });
      },
      [stopwatch, onUpdate],
    );

    const handleNameUpdate = useCallback(
      (newName) => {
        onUpdate({
          name: newName,
        });
      },
      [onUpdate],
    );

    const handleNameEdit = useCallback(() => {
      nameEdit.current.open();
    }, []);

    const ActionsPopup = usePopup(ActionsStep);
    const [t] = useTranslation();

    // Вычисляем стиль бейджа для даты
    const dueDateBadgeStyle = useMemo(() => {
      if (!dueDate) {
        return 'wrapper';
      }
      const dueRange = Math.floor((new Date(dueDate) - new Date()) / (1000 * 60 * 60 * 24));
      if (dueRange < 0) {
        return 'wrapperDeadLine';
      }
      if (dueRange < 6) {
        return 'wrapperSubDeadLine';
      }
      return 'wrapper';
    }, [dueDate]);

    const closedAtLabel = useMemo(() => {
      if (!isClosed) {
        return null;
      }

      const raw = taskClosedAt;
      if (!raw) {
        return null;
      }

      const date = new Date(raw);
      if (Number.isNaN(date.getTime())) {
        return `Дата закрытия: ${String(raw)}`;
      }

      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      return `Дата закрытия: ${day}.${month}.${year}`;
    }, [isClosed, taskClosedAt]);

    const contentNode = (
      <>
        {isClosed && (
          <div className={styles.closedTaskBanner}>
            Задача завершена
          </div>
        )}
        {coverUrl && <img src={coverUrl} alt="" className={styles.cover} />}
        <div className={styles.details}>
          {labels.length > 0 && (
            <span className={styles.labels}>
              {labels.map((label) => (
                <span
                  key={label.id}
                  className={classNames(styles.attachment, styles.attachmentLeft)}
                >
                  <Label name={label.name} color={label.color} size="tiny" />
                </span>
              ))}
            </span>
          )}
          {isPointSystemEnabled && pointsData && pointsData.totalPoints > 0 && (
            <span className={styles.pointsBadgeWrapper}>
              <PointsBadge pointsData={pointsData} />
            </span>
          )}
          {!isPointSystemEnabled && hiddenPointAccrualsTemplateName != null && (
            <HiddenPointAccrualsBadge templateName={hiddenPointAccrualsTemplateName} />
          )}
          {closedUnderPreviousTemplate && previousTemplateName && (
            <span className={styles.previousTemplateBadge} title="Закрыта в другом шаблоне">
              {previousTemplateName}
            </span>
          )}
          <div className={styles.name}>{name}</div>
          {tasks.length > 0 && <Tasks items={tasks} />}
          {(dueDate || stopwatch || notificationsTotal > 0 || (isClosed && closedAtLabel)) && (
            <span className={styles.attachments}>
              {notificationsTotal > 0 && (
                <span
                  className={classNames(
                    styles.attachment,
                    styles.attachmentLeft,
                    styles.notification,
                  )}
                >
                  {notificationsTotal}
                </span>
              )}
              {!isClosed && dueDate && (
                <span className={classNames(styles.attachment, styles.attachmentLeft)}>
                  <span
                    className={classNames(
                      dueDateStyles.wrapper,
                      dueDateStyles[dueDateBadgeStyle],
                      dueDateStyles.wrapperTiny,
                    )}
                  >
                    {(() => {
                      const date = new Date(dueDate);
                      const day = String(date.getDate()).padStart(2, '0');
                      const month = String(date.getMonth() + 1).padStart(2, '0');
                      const year = date.getFullYear();
                      return `Срок задачи: ${day}.${month}.${year}`;
                    })()}
                  </span>
                </span>
              )}
              {isClosed && closedAtLabel && (
                <span className={classNames(styles.attachment, styles.attachmentLeft)}>
                  <span
                    className={classNames(
                      dueDateStyles.wrapper,
                      dueDateStyles.wrapperTiny,
                      styles.closedDateBadge,
                    )}
                  >
                    {closedAtLabel}
                  </span>
                </span>
              )}
              {stopwatch && (
                <span className={classNames(styles.attachment, styles.attachmentLeft)}>
                  <Stopwatch
                    as="span"
                    startedAt={stopwatch.startedAt}
                    total={stopwatch.total}
                    size="tiny"
                    onClick={canEdit ? handleToggleStopwatchClick : undefined}
                  />
                </span>
              )}
            </span>
          )}
          {users.length > 0 && (
            <span className={classNames(styles.attachments, styles.attachmentsRight)}>
              {users.map((user) => (
                <span
                  key={user.id}
                  className={classNames(styles.attachment, styles.attachmentRight)}
                >
                  <User name={user.name} avatarUrl={user.avatarUrl} size="small" />
                </span>
              ))}
            </span>
          )}
        </div>
      </>
    );

    return (
      <Draggable draggableId={`card:${id}`} index={index} isDragDisabled={!isPersisted || !canEdit}>
        {({ innerRef, draggableProps, dragHandleProps }) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <div {...draggableProps} {...dragHandleProps} ref={innerRef} className={styles.wrapper}>
            <NameEdit ref={nameEdit} defaultValue={name} onUpdate={handleNameUpdate}>
              <div className={classNames(styles.card, isClosed && styles.cardClosed)}>
                {isPersisted ? (
                  <>
                    <Link
                      to={Paths.CARDS.replace(':id', id)}
                      className={styles.content}
                      onClick={handleClick}
                    >
                      {contentNode}
                    </Link>
                    {canEdit && (
                      <ActionsPopup
                        card={{
                          dueDate,
                          stopwatch,
                          createdAt,
                          boardId,
                          listId,
                          projectId,
                        }}
                        projectsToLists={allProjectsToLists}
                        boardMemberships={allBoardMemberships}
                        currentUserIds={users.map((user) => user.id)}
                        labels={allLabels}
                        currentLabelIds={labels.map((label) => label.id)}
                        onNameEdit={handleNameEdit}
                        onUpdate={onUpdate}
                        onMove={onMove}
                        onClone={onClone}
                        onTransfer={onTransfer}
                        onDelete={onDelete}
                        onArchive={onArchive}
                        onUserAdd={onUserAdd}
                        onUserRemove={onUserRemove}
                        onBoardFetch={onBoardFetch}
                        onLabelAdd={onLabelAdd}
                        onLabelRemove={onLabelRemove}
                        onLabelCreate={onLabelCreate}
                        onLabelUpdate={onLabelUpdate}
                        onLabelMove={onLabelMove}
                        onLabelDelete={onLabelDelete}
                      >
                        <Button className={classNames(styles.actionsButton, styles.target)}>
                          <Icon fitted name="pencil" size="small" />
                        </Button>
                      </ActionsPopup>
                    )}
                  </>
                ) : (
                  <span className={styles.content}>{contentNode}</span>
                )}
              </div>
            </NameEdit>
          </div>
        )}
      </Draggable>
    );
  },
);

Card.propTypes = {
  id: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  createdAt:PropTypes.string,
  dueDate: PropTypes.instanceOf(Date),
  stopwatch: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  coverUrl: PropTypes.string,
  boardId: PropTypes.string.isRequired,
  listId: PropTypes.string.isRequired,
  projectId: PropTypes.string.isRequired,
  isPersisted: PropTypes.bool.isRequired,
  notificationsTotal: PropTypes.number.isRequired,
  /* eslint-disable react/forbid-prop-types */
  users: PropTypes.array.isRequired,
  labels: PropTypes.array.isRequired,
  tasks: PropTypes.array.isRequired,
  allProjectsToLists: PropTypes.array.isRequired,
  allBoardMemberships: PropTypes.array.isRequired,
  allLabels: PropTypes.array.isRequired,
  /* eslint-enable react/forbid-prop-types */
  canEdit: PropTypes.bool.isRequired,
  isClosed: PropTypes.bool,
  pointsData: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  taskClosedAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  isPointSystemEnabled: PropTypes.bool,
  closedUnderPreviousTemplate: PropTypes.bool,
  previousTemplateName: PropTypes.string,
  hiddenPointAccrualsTemplateName: PropTypes.string,
  onUpdate: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onTransfer: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onArchive: PropTypes.func.isRequired,
  onClone: PropTypes.func.isRequired,
  onUserAdd: PropTypes.func.isRequired,
  onUserRemove: PropTypes.func.isRequired,
  onBoardFetch: PropTypes.func.isRequired,
  onLabelAdd: PropTypes.func.isRequired,
  onLabelRemove: PropTypes.func.isRequired,
  onLabelCreate: PropTypes.func.isRequired,
  onLabelUpdate: PropTypes.func.isRequired,
  onLabelMove: PropTypes.func.isRequired,
  onLabelDelete: PropTypes.func.isRequired,
};

Card.defaultProps = {
  dueDate: undefined,
  stopwatch: undefined,
  coverUrl: undefined,
  isClosed: false,
  isPointSystemEnabled: false,
  closedUnderPreviousTemplate: false,
  previousTemplateName: '',
  hiddenPointAccrualsTemplateName: null,
  taskClosedAt: null,
};

export default Card;
